import { fredSeriesCode, isoDateSchema } from "@inrev/common";
import * as z from "zod";
import { marketSeriesSchema } from "../schemas/market";
import { defineTool } from "../tool-definition";

export const MAX_MARKET_SERIES = 6;

const granularitySchema = z.enum(["year", "quarter", "month"]);

export const getMarketSeries = defineTool({
	name: "get_market_series",
	description: `FRED economic series from Market Analytics, averaged by period. This is economy-wide data, not the record: attribute figures to FRED, and never cite them as the account's. Pass up to ${MAX_MARKET_SERIES} codes exactly as \`list_market_series\` lists them. \`from\` and \`to\` widen to whole periods. Each point is the average of the observations in its \`period\` (the period's first day), in the series' \`units\`; \`observationCount\` is how many observations that average holds, so a latest period with fewer than its siblings is still incomplete. Use \`granularity\` \`quarter\` or \`month\` for a short window such as a recession, \`year\` for long trends. A series with no observations in range has no points. Codes \`list_market_series\` does not list come back in \`unknownSeriesCodes\`. Series show timing and size, not cause: two series moving together does not show that one drove the other.`,
	label: "Market series",
	contexts: ["account", "bond_request", "bond"],
	implementation: "api",
	audience: "model",
	input: z
		.strictObject({
			seriesCodes: z.array(fredSeriesCode).min(1).max(MAX_MARKET_SERIES),
			from: isoDateSchema.optional(),
			to: isoDateSchema.optional(),
			granularity: granularitySchema.default("year"),
		})
		.refine(({ from, to }) => from === undefined || to === undefined || from <= to, {
			message: "`from` must be on or before `to`",
			path: ["to"],
		}),
	data: z.strictObject({
		granularity: granularitySchema,
		items: z.array(
			marketSeriesSchema.extend({
				points: z.array(
					z.strictObject({
						period: isoDateSchema,
						value: z.number(),
						observationCount: z.number().int().positive(),
					}),
				),
			}),
		),
		unknownSeriesCodes: z.array(fredSeriesCode),
	}),
});
